import { useState } from "react";

import { ArrowUpRight, ExternalLink, Undo2 } from "lucide-react";
import Datatable from "@components/datatable";
import StatusChip from "@components/ui-components/status-chip";
import InvoiceDetailModal from "@panel/shared/invoice/components/invoice-detail-modal";
// The payment dialogs are the ops disbursement queue's own, reused as they are so the
// two screens can't drift apart — a payout is recorded the same way whoever makes it.
import UploadUtrModal from "@panel/ops/disbursements/components/upload-utr-modal";
import RevertPaymentModal from "@panel/ops/disbursements/components/revert-payment-modal";
import useBrandRouteGuard from "../use-brand-route-guard";
import useBrandDisbursements from "./use-brand-disbursements";
import { CURRENCY, INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, KYC_LABEL, KYC_TONE } from "@constants/panel/common";
import { CONTRACT_STATUS, CONTRACT_STATUS_LABEL, CONTRACT_STATUS_TONE } from "@panel/shared/contract/misc/constants";
import { formatCurrency } from "@helpers/utils";
import { formatDate } from "@helpers/date";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

// Same gate as the ops queue: an invoice is payable until it is paid, and only a paid
// one can be reverted.
const PAYABLE_STATUSES = ["eligible", "reverted", "pending"];

// Mirrors the ops disbursement queue's own gate: a publisher invoice can't be paid
// until KYC is verified and the contract acknowledged, whoever is recording the payment.
const kycLabelLower = (kycStatus) => (KYC_LABEL[kycStatus] || kycStatus || "").toLowerCase();
const contractLabelLower = (contractStatus) => (CONTRACT_STATUS_LABEL[contractStatus] || contractStatus || "").toLowerCase();

const isPayable = (row) => row.kyc_status === "verified" && row.contract_status === CONTRACT_STATUS.ACKNOWLEDGED;

const holdReason = (row) => {
    const reasons = [];
    if (row.kyc_status !== "verified") {
        reasons.push(`KYC ${kycLabelLower(row.kyc_status)}`);
    }
    if (row.contract_status !== CONTRACT_STATUS.ACKNOWLEDGED) {
        reasons.push(`Contract ${contractLabelLower(row.contract_status)}`);
    }
    return reasons.join(" · ");
};

// The payables queue for a self-payout brand: the publisher invoices it settles
// directly. A managed-payout brand pays Nook Pay against a debit note instead, so the
// guard bounces it the same way the nav swaps the two links.
const BrandDisbursements = () => {
    const { brandId, brand, allowed } = useBrandRouteGuard({ requiresSelfPayout: true });
    // Only fetch once the guard confirms this brand belongs here — switching to a
    // managed-payout brand rewrites :brandId in place, so without this the invoices
    // call fires for a brand that has no payables queue before the redirect lands.
    const {
        invoices, loading, pagination, totalAmount, handlePageChange, handlePerRowsChange,
        utrTarget, revertTarget, openUtrModal, closeUtrModal, openRevertModal, closeRevertModal,
        uploadUTR, revertPayment
    } = useBrandDisbursements(allowed ? brandId : null);

    const [detailTarget, setDetailTarget] = useState(null);

    const columns = [
        {
            Header  : "Invoice",
            accessor: "invoice_number",
            // The stored tax invoice is one click away from the row itself; the
            // action column's modal is for everything around it.
            Cell    : ({ value, row }) => (row.invoice_url ? (
                <a
                    href={row.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.invoice_meta?.original_name || row.invoice_meta?.name || "Tax invoice"}
                    className="inline-flex items-center gap-1.5 font-medium text-gray-900 hover:text-primary hover:underline">
                    {value}
                    <ExternalLink className="h-3.5 w-3.5" />
                </a>
            ) : <span className="font-medium text-gray-900">{value}</span>)
        },
        { Header: "Publisher", accessor: "holder_name", Cell: ({ value }) => <span className="text-gray-900">{value || "—"}</span> },
        {
            Header  : "KYC",
            accessor: "kyc_status",
            Cell    : ({ value }) => <StatusChip className="max-w-[9rem]" tone={KYC_TONE[value] || "neutral"} label={KYC_LABEL[value] || value || "—"} />
        },
        {
            Header  : "Contract",
            accessor: "contract_status",
            Cell    : ({ value }) => <StatusChip className="max-w-[9rem]" tone={CONTRACT_STATUS_TONE[value] || "neutral"} label={CONTRACT_STATUS_LABEL[value] || value || "—"} />
        },
        { Header: "Period", accessor: "payout_period", Cell: ({ value }) => <span className="text-gray-700">{value || "—"}</span> },
        { Header: "Issued", accessor: "issue_date", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span> },
        { Header: "Due", accessor: "due_date", Cell: ({ value }) => <span className="text-gray-700">{value ? formatDate(value) : "—"}</span> },
        { Header: "Status", accessor: "status", Cell: ({ value }) => <StatusChip tone={INVOICE_STATUS_TONE[value] || "neutral"} label={INVOICE_STATUS_LABEL[value] || value} /> },
        { Header: "Reference", accessor: "payment_reference", Cell: ({ value }) => <span className="text-gray-700">{value || "—"}</span> },
        {
            Header  : "Amount",
            accessor: "amount",
            Cell    : ({ value, row }) => <span className="font-medium text-gray-900">{formatCurrency(value, row.currency || CURRENCY)}</span>
        },
        {
            Header  : "Action",
            accessor: "_id",
            Cell    : ({ row }) => (
                <div className="flex items-center gap-2">
                    {row.status === "paid" ? (
                        <button
                            type="button"
                            onClick={() => openRevertModal(row)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                            <Undo2 className="h-3.5 w-3.5" />
                            Revert payment
                        </button>
                    ) : PAYABLE_STATUSES.includes(row.status) && (
                        <button
                            type="button"
                            disabled={!isPayable(row) || (Boolean(row.eligibility) && row.eligibility !== "eligible")}
                            title={!isPayable(row) ? `Held — ${holdReason(row)}` : (row.eligibility && row.eligibility !== "eligible" ? "Publisher isn't eligible for payout yet" : "")}
                            onClick={() => openUtrModal(row)}
                            className="rounded-lg bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-50">
                            Record Payment
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setDetailTarget(row)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50">
                        View invoice
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">Disbursements</h1>
            <p className="mt-1 text-sm text-muted">
                Invoices your publishers have raised. You pay these directly — Nook Pay tracks them for your records.
            </p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:max-w-xl">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Invoices</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{pagination.total}</p>
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-muted">Total amount</p>
                    <p className="mt-1 text-2xl font-bold text-gray-900">{formatCurrency(totalAmount, CURRENCY)}</p>
                </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <Datatable
                    columns={columns}
                    data={invoices}
                    loading={loading}
                    pagination={{ count: pagination.total, page: pagination.page, limit: pagination.limit }}
                    paginationRowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                    handlePageChange={handlePageChange}
                    handlePerRowsChange={handlePerRowsChange}
                    customNoData={<span className="text-sm text-muted">No publisher invoices yet.</span>} />
            </div>

            <InvoiceDetailModal
                open={Boolean(detailTarget)}
                onClose={() => setDetailTarget(null)}
                // The modal's subtitle names the account the invoice was raised against —
                // the rows don't carry it here, every one belongs to the brand in the URL.
                invoice={detailTarget && { ...detailTarget, account_name: brand?.name }} />

            <UploadUtrModal
                open={Boolean(utrTarget)}
                onClose={closeUtrModal}
                publisherName={utrTarget?.holder_name}
                amount={utrTarget?.amount}
                onConfirm={uploadUTR} />

            <RevertPaymentModal
                open={Boolean(revertTarget)}
                onClose={closeRevertModal}
                publisherName={revertTarget?.holder_name}
                amount={revertTarget?.amount}
                onRevert={revertPayment} />
        </div>
    );
};

export default BrandDisbursements;
